/**
 * @fileoverview Core type definitions for the job management system
 * 
 * This file contains all TypeScript interfaces and types related to:
 * - Jobs: Main project/job entities that flow through the procurement workflow
 * - RFQs: Request for Quotation entities for soliciting bids from vendors
 * - Bids: Vendor responses to RFQs with pricing and delivery commitments
 * - Awards: Final selection of winning bid and contract establishment
 * - Purchase Orders: Formal purchasing documents and vendor acknowledgments
 * - Delivery Proofs: Evidence of completed delivery with photos and signatures
 * - Specifications: Product/service requirements and technical details
 * - Delivery Details: Location information and delivery instructions
 * 
 * These types represent the entire lifecycle from job creation through delivery
 * acceptance, including multi-party approval workflows and modification tracking.
 * 
 * @module lib/types/job
 */

import { CancellationDocument } from './cancellation';

/**
 * Job status in the overall procurement workflow
 * 
 * - draft: Initial state, job being created
 * - rfq_open: Request for Quotation is open for bidding
 * - awarded: Winning bid selected, contract being prepared
 * - in_progress: Work has begun on the job
 * - completed_pending_review: Work submitted, awaiting client review
 * - accepted: Client accepted the completed work
 * - rejected: Client rejected the work, may allow resubmission
 * - disputed: Formal dispute opened between parties
 * - closed: Job finalized and archived
 */
export type JobStatus =
  | 'draft' 
  | 'rfq_open' 
  | 'awarded' 
  | 'in_progress' 
  | 'completed_pending_review' 
  | 'accepted' 
  | 'rejected' 
  | 'disputed' 
  | 'closed';

/**
 * Workflow stage tracking for job progression
 * 
 * Represents the current stage in the job workflow, which may differ from status.
 * Used to guide users through required steps and validate stage transitions.
 */
export type WorkflowStage =
  | 'draft' 
  | 'specifications' 
  | 'delivery_details' 
  | 'rfq_draft'
  | 'rfq_ready' 
  | 'rfq_open' 
  | 'awarded' 
  | 'purchase_order' 
  | 'in_progress' 
  | 'delivery_proof' 
  | 'completed' 
  | 'closed';

/** Job priority level for internal tracking and SLA management */
export type PriorityLevel = 'low' | 'medium' | 'high' | 'urgent';

/** Budget approval status in the client's internal approval workflow */
export type BudgetApprovalStatus = 'pending' | 'approved' | 'rejected' | 'revision_required';

/** Method of vendor procurement */
export type ProcurementMethod = 'rfq' | 'direct_award' | 'competitive_bid' | 'sole_source';

/** Status of an RFQ in its lifecycle */
export type RFQStatus = 'draft' | 'open' | 'closed' | 'awarded';

/** Type of RFQ for visibility control */
export type RFQType = 'open' | 'invited' | 'direct';

/** Visibility setting for RFQs - public (all verified vendors) or private (invited only) */
export type RFQVisibilityType = 'public' | 'private';

/**
 * Public RFQ targeting criteria for vendor matching
 * 
 * Defines requirements that vendors must meet to see and bid on a public RFQ.
 * Used to calculate match scores and filter the vendor pool automatically.
 */
export interface PublicRFQCriteria {
  id: string;
  rfq_id: string;
  required_certification_type_ids?: string[];
  min_rating?: number;
  min_completed_jobs?: number;
  min_verification_level?: 'basic' | 'enhanced' | 'premium';
  estimated_matching_vendors?: number;
  last_match_calculation_at?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Vendor match score result with granular scoring
 * 
 * Calculated score indicating how well a vendor meets the RFQ criteria.
 * Uses proportional scoring: 90%+ = perfect match, each criterion scored 0-1.
 * 
 * Scoring weights: Certifications (35%), Rating (25%), Experience (25%), Verification (15%)
 */
export interface VendorMatchScore {
  total_criteria: number;
  criteria_met: number;
  score: number;
  criterion_scores?: {
    certifications_score?: number;
    rating_score?: number;
    jobs_score?: number;
    verification_score?: number;
  };
  details: {
    no_criteria?: boolean;
    not_verified?: boolean;
    certifications_required?: string[];
    certifications_matched?: number;
    certifications_met?: boolean;
    certifications_score?: number;
    min_rating_required?: number;
    vendor_rating?: number;
    rating_met?: boolean;
    rating_score?: number;
    min_jobs_required?: number;
    vendor_completed_jobs?: number;
    jobs_met?: boolean;
    jobs_score?: number;
    min_verification_level?: string;
    vendor_verification_level?: string;
    verification_met?: boolean;
    verification_score?: number;
  };
}

/**
 * Vendor match result with detailed breakdown
 * 
 * More detailed matching information including which specific criteria were met/missed.
 */
export interface VendorMatchResult {
  matches: boolean;
  details: {
    certifications_matched?: number;
    certifications_required?: number;
    certifications_met?: boolean;
    vendor_rating?: number;
    min_rating_required?: number;
    rating_met?: boolean;
    vendor_completed_jobs?: number;
    min_jobs_required?: number;
    jobs_met?: boolean;
    vendor_verification_level?: string;
    min_verification_level?: string;
    verification_met?: boolean;
  };
}

/**
 * Job category classification
 * 
 * Used for organizing jobs, matching vendors by expertise, and reporting.
 */
export type JobCategory =
  | 'print_materials' 
  | 'packaging' 
  | 'signage' 
  | 'merchandising' 
  | 'other';

/**
 * Main job entity
 * 
 * Represents a procurement project from creation through completion.
 * Contains all job metadata, workflow tracking, and relationships to
 * specifications, RFQs, purchase orders, and delivery proofs.
 */
export interface Job {
  id: string;
  public_id: string;
  organization_id: string;
  created_by_user_id?: string;
  copied_from_job_id?: string;
  title: string;
  description: string;
  category: JobCategory;
  budget_cents: number;
  art_files_ready_date?: string;
  due_date: string;
  status: JobStatus;
  // Enhanced fields for new workflow
  project_name?: string;
  internal_reference?: string;
  workflow_stage: WorkflowStage;
  requirements_complete: boolean;
  priority_level?: PriorityLevel;
  specifications_required: boolean;
  delivery_details_required: boolean;
  client_notes?: string;
  estimated_start_date?: string;
  estimated_completion_date?: string;
  budget_currency: string;
  budget_approval_status: BudgetApprovalStatus;
  procurement_method: ProcurementMethod;
  vendor_selection_criteria: any;
  compliance_requirements: any[];
  risk_assessment: any;
  // Payment fields
  payment_status?: 'not_required' | 'payment_pending' | 'ach_pending' | 'payment_captured' | 'held_in_escrow' | 'paid_to_vendor' | 'payment_failed';
  payment_transaction_id?: string;
  created_at: string;
  updated_at: string;
  // Related entities
  organization?: {
    id: string;
    name: string;
  };
  rfq?: RFQ;
  specifications?: JobSpecification[];
  delivery_details?: JobDeliveryDetails;
  purchase_order?: PurchaseOrder;
  delivery_proof?: DeliveryProof;
  awards?: {
    id: string;
    winning_bid_id: string;
    total_award_amount_cents?: number;
    bids: {
      vendor_user_id: string;
    };
    award_selections?: Array<{
      total_price_cents: number;
    }>;
  } | null;
}

export interface RFQ {
  id: string;
  public_id: string;
  job_id: string;
  revision: number;
  opens_at?: string;
  closes_at: string;
  status: RFQStatus;
  visibility_type?: RFQVisibilityType;
  shipping_terms?: 'fob_origin' | 'fob_destination' | null;
  // Enhanced fields for new workflow
  rfq_type: RFQType;
  invited_vendors: any[];
  vendor_selection_criteria: any;
  minimum_vendor_requirements: any;
  evaluation_criteria: any;
  created_at: string;
  updated_at: string;
  addenda?: RFQAddendum[];
  questions?: RFQQuestion[];
  acknowledgments?: VendorAcknowledgment[];
  public_rfq_criteria?: PublicRFQCriteria;
}

export interface RFQAddendum {
  id: string;
  rfq_id: string;
  revision: number;
  note: string;
  attachments: any[];
  created_by_user_id: string;
  created_at: string;
}

export interface RFQQuestion {
  id: string;
  rfq_id: string;
  question: string;
  asked_by_user_id: string;
  answer?: string;
  answered_by_user_id?: string;
  creates_spec_change: boolean;
  created_at: string;
  answered_at?: string;
}

export interface VendorAcknowledgment {
  id: string;
  rfq_id: string;
  vendor_user_id: string;
  revision: number;
  acknowledged_at: string;
}

export interface VendorProfile {
  id: string;
  user_id: string;
  organization_id: string;
  w9_document_url?: string;
  coi_document_url?: string;
  tax_id?: string;
  is_approved: boolean;
  categories: JobCategory[];
  location?: string;
  typical_budget_min_cents?: number;
  typical_budget_max_cents?: number;
  created_at: string;
  updated_at: string;
}

export const JOB_CATEGORIES: { value: JobCategory; label: string }[] = [
  { value: 'print_materials', label: 'Print Materials' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'signage', label: 'Signage' },
  { value: 'merchandising', label: 'Merchandising' },
  { value: 'other', label: 'Other' },
];

export const getStatusColor = (status: JobStatus | 'rfq_closed'): string => {
  const colors: Record<string, string> = {
    draft: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    rfq_open: 'bg-green-100 text-green-800 border-green-200',
    rfq_closed: 'bg-amber-100 text-amber-800 border-amber-200',
    awarded: 'bg-blue-100 text-blue-800 border-blue-200',
    in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
    completed_pending_review: 'bg-orange-100 text-orange-800 border-orange-200',
    accepted: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    rejected: 'bg-red-100 text-red-800 border-red-200',
    disputed: 'bg-gray-100 text-gray-800 border-gray-200',
    closed: 'bg-gray-100 text-gray-800 border-gray-200',
  };
  return colors[status] || colors.closed;
};

export const formatCurrency = (cents: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

export const parseCurrency = (value: string): number => {
  const cleanValue = value.replace(/[^0-9.]/g, '');
  return Math.round(parseFloat(cleanValue || '0') * 100);
};

/**
 * Sub-steps within the "awarded" status for visual tracking
 * These do not change the job status, only provide visual indicators
 */
export type AwardedSubStep = 
  | 'milestone_planning'
  | 'pending_po' 
  | 'po_sent'
  | 'awaiting_vendor';

/**
 * Determines the current sub-step for an awarded job based on PO status
 * Used for display purposes only - does not affect business logic
 */
export const getAwardedSubStep = (job: Job): AwardedSubStep | null => {
  if (job.status !== 'awarded') return null;
  
  const po = Array.isArray(job.purchase_order) 
    ? job.purchase_order[0] 
    : job.purchase_order;
  
  if (!po) {
    // No PO yet - still in milestone planning phase
    return 'milestone_planning';
  }
  
  if (po.status === 'draft' || po.status === 'pending_approval') {
    return 'pending_po';
  }
  
  if (po.status === 'sent_to_vendor') {
    return 'awaiting_vendor';
  }
  
  // PO has been acknowledged or is active
  return 'po_sent';
};

/**
 * Configuration for awarded sub-step badges
 * Returns styling and display information for each sub-step
 */
export const getSubStepConfig = (subStep: AwardedSubStep): {
  label: string;
  icon: 'Calendar' | 'FileText' | 'Send' | 'Clock';
  className: string;
} => {
  const config = {
    milestone_planning: {
      label: 'Planning',
      icon: 'Calendar' as const,
      className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800'
    },
    pending_po: {
      label: 'PO Draft',
      icon: 'FileText' as const,
      className: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-700'
    },
    po_sent: {
      label: 'PO Issued',
      icon: 'Send' as const,
      className: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950 dark:text-teal-300 dark:border-teal-800'
    },
    awaiting_vendor: {
      label: 'Awaiting Vendor',
      icon: 'Clock' as const,
      className: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800'
    }
  };
  return config[subStep];
};

// Bidding system types
export type BidStatus = 'submitted' | 'withdrawn' | 'winner' | 'loser';

export interface Bid {
  id: string;
  public_id: string;
  rfq_id: string;
  vendor_user_id: string;
  price_cents?: number; // Optional: Calculated from bid_line_items.total_price_cents
  currency: string;
  lead_time_days: number;
  notes?: string | null;
  attachments: any;
  valid_until: string;
  attestation_data: any;
  status: BidStatus;
  created_at: string;
  updated_at: string;
  vendor_profile?: VendorProfile;
  total_vendor_tax_cents?: number | null;
  tax_calculation_status?: string | null;
}

export interface Award {
  id: string;
  job_id: string;
  winning_bid_id: string;
  client_po_ref?: string;
  award_notes?: string;
  awarded_by_user_id: string;
  awarded_at: string;
  created_at: string;
  winning_bid?: Bid;
}

export interface BidSubmissionData {
  price_cents: number;
  lead_time_days: number;
  notes?: string;
  attachments?: any;
  valid_until: string;
  attestation_data: any;
}

// New workflow system types

export type SpecificationType = 'my_organization' | 'personal';
export type ComplianceLevel = 'required' | 'preferred' | 'optional';
export type LocationType = 'office' | 'warehouse' | 'job_site' | 'distribution_center' | 'retail_location' | 'other';

export interface SpecificationTemplate {
  id: string;
  public_id: string;
  organization_id: string;
  specification_name: string;
  specification_description?: string;
  category: JobCategory;
  specification_type: SpecificationType;
  content: any;
  sections: any[];
  required_fields: any[];
  is_active: boolean;
  version: number;
  created_by_user_id: string;
  last_updated_by_user_id: string;
  usage_count: number;
  tags: any[];
  created_at: string;
  updated_at: string;
}

export interface JobSpecification {
  id: string;
  job_id: string;
  specification_order: number;
  source_template_id?: string;
  specification_name: string;
  specification_type: SpecificationType;
  content: any;
  sections: any[];
  custom_requirements: any;
  is_mandatory: boolean;
  compliance_level: ComplianceLevel;
  created_by_user_id: string;
  last_updated_by_user_id: string;
  created_at: string;
  updated_at: string;
  source_template?: SpecificationTemplate;
}

export interface DeliveryLocationTemplate {
  id: string;
  public_id: string;
  organization_id: string;
  location_name: string;
  location_type: LocationType;
  address_line1: string;
  address_line2?: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  latitude?: number | null;
  longitude?: number | null;
  geocode_precision?: string | null;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  special_instructions?: string;
  access_requirements?: string;
  delivery_hours: any;
  preferred_carriers: any[];
  restricted_carriers: any[];
  dock_information?: string;
  is_active: boolean;
  usage_count: number;
  address_validated?: boolean | null;
  address_validated_at?: string | null;
  address_deliverability?: string | null;
  created_by_user_id: string;
  last_updated_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export type DeadlineType = 'hard_deadline' | 'target_date' | 'flexible_date';

export interface JobDeliveryDetails {
  id: string;
  job_id: string;
  source_template_id?: string;
  delivery_name: string;
  location_type: LocationType;
  address_line1: string;
  address_line2?: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  delivery_window_start?: string;
  delivery_window_end?: string;
  preferred_delivery_date?: string;
  deadline_type?: DeadlineType;
  special_instructions?: string;
  access_requirements?: string;
  delivery_hours: any;
  preferred_carriers: any[];
  restricted_carriers: any[];
  dock_information?: string;
  signature_required: boolean;
  id_required: boolean;
  appointment_required: boolean;
  created_by_user_id: string;
  last_updated_by_user_id: string;
  created_at: string;
  updated_at: string;
  source_template?: DeliveryLocationTemplate;
  // Purpose flags to distinguish product vs sample destinations
  is_product_destination: boolean;
  is_sample_destination: boolean;
}

export type POStatus = 'draft' | 'pending_approval' | 'approved' | 'sent_to_vendor' | 'acknowledged' | 'modification_requested' | 'rejected' | 'cancelling' | 'cancelled' | 'completed';
export type POModificationRequestType = 'price_change' | 'delivery_change' | 'specification_change' | 'terms_change' | 'cancellation';
export type POModificationResponseType = 'accept' | 'modify' | 'decline';
export type POModificationStatus = 'pending' | 'accepted' | 'modified' | 'declined' | 'withdrawn' | 'ach_pending_settlement';

export type CancellationStatus = 
  | 'pending_vendor_response'
  | 'pending_client_approval'
  | 'client_accepted'
  | 'disputed'
  | 'vendor_declined'
  | 'completed';

export interface PurchaseOrder {
  id: string;
  public_id: string;
  job_id: string;
  winning_bid_id: string;
  organization_id: string;
  vendor_organization_id?: string;
  po_number: string;
  po_amount_cents: number;
  currency: string;
  status: POStatus;
  terms_and_conditions?: string;
  payment_terms?: string;
  delivery_schedule: any;
  line_items: any[];
  vendor_acknowledgment_required: boolean;
  vendor_acknowledged_at?: string;
  vendor_acknowledged_by_user_id?: string;
  vendor_response?: string;
  modification_requests: any[];
  contract_attachments: any[];
  special_instructions?: string;
  billing_address: any;
  shipping_address: any;
  created_by_user_id: string;
  approved_by_user_id?: string;
  approved_at?: string;
  sent_to_vendor_at?: string;
  created_at: string;
  updated_at: string;
  winning_bid?: Bid;
  job?: Job;
}

export interface POModificationRequest {
  id: string;
  purchase_order_id: string;
  request_type: POModificationRequestType;
  requested_by_user_id: string;
  request_details: any;
  adjustment_amount_cents?: number;
  adjustment_reason?: string;
  modification_reason?: string;
  initiating_party?: 'client' | 'vendor';
  requires_approval?: boolean;
  vendor_response?: string;
  vendor_response_type?: POModificationResponseType;
  responded_by_user_id?: string;
  responded_at?: string;
  status: POModificationStatus;
  payment_status?: string;
  payment_transaction_id?: string;
  payment_initiated_at?: string;
  payment_completed_at?: string;
  applied_at?: string;
  auto_approved?: boolean;
  correlation_id?: string;
  purchase_order?: PurchaseOrder;
  created_at: string;
  updated_at: string;
  // Cancellation-specific fields
  cancellation_charge_cents?: number;
  cancellation_fee_explanation?: string;
  cancellation_supporting_documents?: CancellationDocument[] | any; // Json from DB, CancellationDocument[] in app
  vendor_response_submitted_at?: string;
  client_approval_decision?: 'accepted' | 'disputed';
  client_responded_at?: string;
  cancellation_status?: CancellationStatus;
}

export type AdjustmentType = 'credit' | 'debit';

export type DeliveryMethod = 'standard' | 'express' | 'overnight' | 'freight' | 'pickup' | 'direct' | 'other';
export type DeliveryCondition = 'good' | 'damaged' | 'incomplete' | 'wrong_items' | 'other';
export type DeliveryReviewStatus = 'pending' | 'accepted' | 'rejected' | 'needs_correction' | 'auto_accepted';
export type DeliveryExceptionType = 'damaged' | 'missing' | 'wrong_quantity' | 'wrong_item' | 'late_delivery' | 'access_issue' | 'other';
export type DeliveryExceptionSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface DeliveryProof {
  id: string;
  public_id: string;
  job_id: string;
  purchase_order_id?: string;
  organization_id: string;
  submitted_by_user_id: string;
  delivery_date: string;
  delivery_time?: string;
  carrier_name?: string;
  tracking_number?: string;
  delivery_method?: DeliveryMethod;
  recipient_name: string;
  recipient_signature_url?: string;
  delivery_location: any;
  delivery_photos: any[];
  delivery_receipts: any[];
  packing_slip_url?: string;
  invoice_reference?: string;
  delivery_notes?: string;
  condition_on_delivery: DeliveryCondition;
  exceptions_noted?: string;
  partial_delivery: boolean;
  items_delivered: any[];
  client_review_status: DeliveryReviewStatus;
  client_reviewed_by_user_id?: string;
  client_reviewed_at?: string;
  client_review_notes?: string;
  resubmission_count: number;
  max_resubmissions: number;
  auto_accept_at: string;
  created_at: string;
  updated_at: string;
  job?: Job;
  purchase_order?: PurchaseOrder;
  exceptions?: DeliveryException[];
}

export interface DeliveryException {
  id: string;
  delivery_proof_id: string;
  exception_type: DeliveryExceptionType;
  description: string;
  severity: DeliveryExceptionSeverity;
  resolution_required: boolean;
  resolution_notes?: string;
  resolved_at?: string;
  resolved_by_user_id?: string;
  photos: any[];
  created_by_user_id: string;
  created_at: string;
  delivery_proof?: DeliveryProof;
}

// RFQ Decline System
export const DECLINE_REASONS = [
  "Not enough time to bid",
  "Not a match for our capabilities",
  "Timeline too short",
  "No capacity at this time",
  "Delivery requirements issue",
  "Specifications are not clear",
  "Not interested at this time"
] as const;

export type DeclineReason = typeof DECLINE_REASONS[number];

// Shipment Types
export type ShipmentStatus = 
  | 'preparing'
  | 'shipped'
  | 'in_transit'
  | 'out_for_delivery'
  | 'delivered'
  | 'exception'
  | 'cancelled';

export interface JobShipment {
  id: string;
  public_id: string;
  job_id: string;
  delivery_location_id?: string;
  organization_id: string;
  carrier_name?: string;
  tracking_numbers: string[];
  ship_date?: string;
  estimated_delivery_date?: string;
  actual_delivery_date?: string;
  shipment_status: ShipmentStatus;
  status_notes?: string;
  items_shipped?: any[];
  created_by_user_id: string;
  last_updated_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export const SHIPMENT_STATUSES: { value: ShipmentStatus; label: string; description: string; color: string }[] = [
  { value: 'preparing', label: 'Preparing', description: 'Shipment is being prepared', color: 'secondary' },
  { value: 'shipped', label: 'Shipped', description: 'Package has been shipped', color: 'default' },
  { value: 'in_transit', label: 'In Transit', description: 'Package is in transit', color: 'default' },
  { value: 'out_for_delivery', label: 'Out for Delivery', description: 'Package is out for delivery', color: 'default' },
  { value: 'delivered', label: 'Delivered', description: 'Package has been delivered', color: 'default' },
  { value: 'exception', label: 'Exception', description: 'Delivery exception occurred', color: 'destructive' },
  { value: 'cancelled', label: 'Cancelled', description: 'Shipment was cancelled', color: 'secondary' },
];

export interface CreateShipmentData {
  delivery_location_id?: string;
  carrier_name?: string;
  tracking_numbers: string[];
  ship_date?: Date;
  estimated_delivery_date?: Date;
  shipment_status: ShipmentStatus;
  status_notes?: string;
}

export interface RFQDecline {
  id: string;
  rfq_id: string;
  vendor_user_id: string;
  decline_reason: DeclineReason;
  decline_notes?: string;
  declined_at: string;
  created_at: string;
}